import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { getNominationRoundYear } from '../../../../utils/getNominationRoundYear.js';
import { devError } from '../../../../utils/devLog';

const COIModal = ({
  show,
  onHide,
  selectedCandidate,
  coiType,
  coiDetail,
  coiValidationError,
  coiTypeOptions,
  onTypeChange,
  onTypeRemove,
  onDetailChange,
  onSave,
  committeeName = '',
}) => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave();
    } catch (error) {
      devError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuidelinesClose = () => setShowGuidelines(false);
  const handleGuidelinesShow = () => setShowGuidelines(true);

  return (
    <>
      <Modal show={show && !showGuidelines} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCandidate}
            <Button
              variant="link"
              onClick={handleGuidelinesShow}
              className="ms-3 text-decoration-none"
              style={{ fontSize: '1.1em' }}
            >
              📋 Guidelines
            </Button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={
            isDropdownOpen
              ? 'modal-body-dropdown-open'
              : 'modal-body-dropdown-closed'
          }
        >
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                A user can select multiple direct conflicts, or multiple
                indirect conflicts but not a combination of both
              </Form.Label>
              <div className="mb-2">
                {coiType.map((type) => {
                  const option = coiTypeOptions.find(
                    (opt) => opt.value === type,
                  );
                  return (
                    <span key={type} className="badge bg-primary me-2 mb-1">
                      {option?.label}
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: '0.7em' }}
                        onClick={() => onTypeRemove(type)}
                        aria-label={`Remove ${option?.label}`}
                      />
                    </span>
                  );
                })}
              </div>
              <Dropdown onToggle={(isOpen) => setIsDropdownOpen(isOpen)}>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  id="coi-type-dropdown"
                >
                  {(() => {
                    if (coiType.length === 0) return 'Select COI Type';
                    if (coiType.includes('no-conflicts'))
                      return 'No conflicts selected';

                    const firstOption = coiTypeOptions.find(
                      (opt) => opt.value === coiType[0],
                    );
                    const category = firstOption?.category;

                    if (category === 'direct') {
                      return `Direct conflicts (${coiType.length})`;
                    }
                    if (category === 'indirect') {
                      return `Indirect conflicts (${coiType.length})`;
                    }

                    return `${coiType.length} selected`;
                  })()}
                </Dropdown.Toggle>
                <Dropdown.Menu className="dropdown-menu-scrollable">
                  {(() => {
                    const getFilteredOptions = (category) =>
                      coiTypeOptions
                        .filter(
                          (option) =>
                            option.category === category &&
                            !coiType.includes(option.value),
                        )
                        .sort((a, b) => {
                          const orderA = a.order != null ? a.order : 999;
                          const orderB = b.order != null ? b.order : 999;
                          if (orderA !== orderB) return orderA - orderB;
                          return (a.label || '').localeCompare(b.label || '');
                        });

                    const renderItems = (options) =>
                      options.map((option) => (
                        <Dropdown.Item
                          key={option.value}
                          onClick={() => onTypeChange(option.value)}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ));

                    const noneOptions = getFilteredOptions('none');
                    const directOptions = getFilteredOptions('direct');
                    const indirectOptions = getFilteredOptions('indirect');
                    const hasConflictOptions =
                      directOptions.length > 0 || indirectOptions.length > 0;

                    return (
                      <>
                        {renderItems(noneOptions)}

                        {noneOptions.length > 0 && hasConflictOptions && (
                          <Dropdown.Divider />
                        )}

                        {directOptions.length > 0 && (
                          <>
                            <Dropdown.Header className="fw-bold">
                              Direct Conflicts
                            </Dropdown.Header>
                            {renderItems(directOptions)}
                          </>
                        )}

                        {directOptions.length > 0 &&
                          indirectOptions.length > 0 && <Dropdown.Divider />}

                        {indirectOptions.length > 0 && (
                          <>
                            <Dropdown.Header className="fw-bold">
                              Indirect Conflicts
                            </Dropdown.Header>
                            {renderItems(indirectOptions)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>

            {coiValidationError && (
              <div
                className={`alert alert-danger ${isDropdownOpen ? 'hidden-when-dropdown-open' : ''}`}
                role="alert"
              >
                {coiValidationError}
              </div>
            )}

            {!coiType.includes('no-conflicts') && coiType.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>COI Detail</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={coiDetail}
                  onChange={(e) => onDetailChange(e.target.value)}
                  maxLength={300}
                  placeholder="Provide details to support the declared conflict of interest..."
                  className="h-auto"
                />
                <Form.Text className="text-muted">
                  {coiDetail.length}/300 characters
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="success"
            className="standard-width-button"
            onClick={handleSave}
            disabled={coiType.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showGuidelines}
        onHide={handleGuidelinesClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>COI Guidelines</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 text-center">
            <h4>
              <strong>{committeeName || 'Sectional Committee'}</strong>
            </h4>
            <h5>
              <strong>{getNominationRoundYear(new Date())} Round</strong>
            </h5>
          </div>

          <p>
            Please indicate any direct or indirect conflicts and also whether
            you have any history of animosity with any of the candidates. Please
            review the{' '}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() =>
                window.open(
                  'https://www.science.org.au/files/userfiles/fellowship/documents/FNS_Conflicts_of_Interest.pdf',
                  '_blank',
                )
              }
            >
              <i className="bi bi-link" /> Guidelines
            </button>{' '}
            All COI fields must be completed, including where there is no
            conflict. If you enter a COI please add details to support the COI.
          </p>
        </Modal.Body>
      </Modal>
    </>
  );
};

COIModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedCandidate: PropTypes.string,
  coiType: PropTypes.array.isRequired,
  coiDetail: PropTypes.string.isRequired,
  coiValidationError: PropTypes.string.isRequired,
  coiTypeOptions: PropTypes.array.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  onTypeRemove: PropTypes.func.isRequired,
  onDetailChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  committeeName: PropTypes.string,
};

export default COIModal;
